import { useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PublishAdCreative } from "@/screens/creative-library/types";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  Table as ReactTable,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Button } from "@/components/ui/button";
import { RxSize } from "react-icons/rx";
import GalleryView from "../../../GalleryView";
import emptyStateSvg from "@/assets/creativeLibraryAssets/publishTableEmptyState.svg";
import AdCard from "@/components/common/adCard";
import DownloadAction from "@/components/common/adCard/actions/DownloadAction";
import { renderToString } from "react-dom/server";
import { IconCards } from "@tabler/icons-react";
import { useGlobalStore } from "@/GlobalStore";
import WaveLoader from "@/components/common/loaders/WaveLoader";
import { getMediaIcon } from "@/screens/utils/Helpers";

interface Props {
  data: PublishedTableData[];
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

export type PublishedTableData = {
  ad: PublishAdCreative;
  publishingAsset: PublishingAsset;
  primaryText: string[];
  publishingDate: string;
};

export type PublishingAsset = {
  campaign: {
    id: string;
    name: string;
  };
  adset: {
    id: string;
    name: string;
  };
};

const RenderPublishAdCreative = ({
  publishAdCreative,
}: {
  publishAdCreative: PublishAdCreative;
}) => {
  const [openGallery, setOpenGallery] = useState<boolean>(false);
  const baseCreative = useMemo(
    () => publishAdCreative.ad_creative,
    [publishAdCreative.ad_creative],
  );
  const imageUrl =
    publishAdCreative?.image_url ||
    publishAdCreative?.ad_creative?.creative?.images[0]?.permalink_url ||
    publishAdCreative?.ad_creative?.creative?.videos[0]?.thumbnail_url;
  const creativeType = publishAdCreative?.ad_creative?.creative_type;
  const { selectedAccount } = useGlobalStore();
  const galleryItems = useMemo(() => {
    if (baseCreative) {
      const images = baseCreative.creative?.images || [];
      return images.length
        ? images.map((img) => ({
            ...baseCreative,
            id: `${baseCreative.id}-${img.id}`,
            creative: { ...baseCreative.creative, images: [img] },
          }))
        : [baseCreative];
    }
    return publishAdCreative.ad_creatives || [];
  }, [baseCreative, publishAdCreative.ad_creatives]);

  return (
    <div className="flex gap-2 items-center min-w-[300px] py-2">
      <div className="w-[67px] h-[67px] rounded-[8px] overflow-hidden">
        <img className="w-full h-full object-cover" src={imageUrl} alt="" />
      </div>
      <div className="flex flex-col gap-2">
        <span className="font-inter font-normal text-[14px] leading-[20px] tracking-[0%] text-brand-primary-new truncate max-w-[184px] overflow-hidden text-ellipsis">
          {baseCreative?.name || ""}
        </span>
        <div className="flex gap-2 items-center">
          {getMediaIcon(creativeType, true, 16, 32)}
          <Button
            variant={"outline"}
            className="flex items-center gap-1"
            onClick={() => setOpenGallery(true)}
          >
            <IconCards size={16} color="#021859CC" />
            <span className="text-brand-primary-800 font-normal text-[12px] leading-[19.16px] tracking-[2%]">
              Show Ad
            </span>
          </Button>
        </div>
      </div>
      <GalleryView
        open={openGallery}
        onOpenChange={setOpenGallery}
        items={galleryItems}
        renderItem={(ad) => (
          <AdCard
            header={{
              icon:
                ad?.creative?.account_icon_url ||
                selectedAccount?.additional_info?.ad_account_icon_url,
              title: ad?.creative?.account_name ?? "",
            }}
            body={{
              icon_src: `data:image/svg+xml;base64,${btoa(renderToString(<RxSize size={18} color="#021859CC" />))}`,
              name: ad.creative?.images?.[0]?.aspect_ratio ?? "",
              description: ad?.creative.bodies?.[0],
              media: {
                ad_id: ad.id || "",
                source: ad.creative?.videos?.[0]
                  ? ad.creative?.videos?.[0]?.permalink_url
                  : ad.creative?.images?.[0]?.permalink_url,
                type: ad.creative?.videos?.[0] ? "video" : "image",
                thumbnail: ad.creative.thumbnail_url,
                actions: [
                  {
                    component: (
                      <DownloadAction
                        url={
                          ad.creative_type === "video"
                            ? ad.creative?.videos?.[0]?.permalink_url
                            : ad.creative?.images?.[0]?.uncompressed_url ||
                              ad.creative?.images?.[0]?.permalink_url
                        }
                        type={ad.creative_type}
                        aspectRatio={
                          ad.creative_type === "video"
                            ? ad.creative?.videos?.[0]?.aspect_ratio
                            : ad.creative?.images?.[0]?.aspect_ratio
                        }
                      />
                    ),
                    position: "top-right",
                    visibility: "hover",
                  },
                ],
              },
            }}
            footer={{
              cta_label: ad.creative.call_to_action_titles?.[0] ?? "",
              landing_page: ad.creative.landing_pages?.[0] ?? "",
              title: ad.creative.titles?.[0] ?? "",
            }}
          />
        )}
      />
    </div>
  );
};

const EmptyState = ({ className }: { className?: string }) => (
  <div className={`h-full flex flex-col items-center justify-center py-10 ${className}`}>
    <img src={emptyStateSvg} alt="No ads published" />
    <div className="font-medium text-[18px] leading-[100%] tracking-[0%] text-center text-brand-primary-new mt-6">
      No ads published yet
    </div>
    <p className="font-normal text-[16px] leading-[100%] tracking-[0%] text-center text-[#617390] mt-3">
      Publish your first ad to start reaching customers
    </p>
  </div>
);

// Virtualized table body component
interface VirtualizedTableBodyProps {
  table: ReactTable<PublishedTableData>;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  enabled?: boolean;
}

function VirtualizedTableBody({
  table,
  tableContainerRef,
  isLoadingMore,
  hasMore,
  onLoadMore,
  enabled = true,
}: VirtualizedTableBodyProps) {
  const { rows } = table.getRowModel();
  const loadMoreTriggeredForCountRef = useRef<number | null>(null);

  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rows.length,
    estimateSize: () => 100, // estimate row height
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== "undefined" && navigator.userAgent.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
    enabled,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  // Reset the trigger ref when new data is loaded
  useEffect(() => {
    if (!isLoadingMore && loadMoreTriggeredForCountRef.current !== null) {
      // Data has been loaded, allow triggering again for new scroll positions
      if (rows.length > loadMoreTriggeredForCountRef.current) {
        loadMoreTriggeredForCountRef.current = null;
      }
    }
  }, [isLoadingMore, rows.length]);

  // Check for pagination when virtualizer scrolls near the end
  useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem) return;

    const shouldLoadMore =
      lastItem.index >= rows.length - 3 &&
      hasMore &&
      !isLoadingMore &&
      onLoadMore &&
      loadMoreTriggeredForCountRef.current !== rows.length;

    if (shouldLoadMore) {
      loadMoreTriggeredForCountRef.current = rows.length;
      onLoadMore();
    }
  }, [virtualItems, rows.length, hasMore, isLoadingMore, onLoadMore]);

  return (
    <tbody
      style={{
        display: "grid",
        height: `${totalSize}px`,
        position: "relative",
      }}
    >
      {virtualItems.map((virtualRow) => {
        const row = rows[virtualRow.index];
        return (
          <TableRow
            key={row.id}
            data-index={virtualRow.index}
            ref={(node) => rowVirtualizer.measureElement(node)}
            className="hover:bg-white flex w-full"
            style={{
              position: "absolute",
              transform: `translateY(${virtualRow.start}px)`,
            }}
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell
                key={cell.id}
                className="border-r last:border-r-0 border-border-gray flex-1 flex items-center"
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        );
      })}
    </tbody>
  );
}

const PublishedTable = (props: Props) => {
  const { data, hasMore, isLoadingMore, onLoadMore } = props;
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isContainerReady, setIsContainerReady] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Track when container is mounted to trigger virtualizer recalculation
  useEffect(() => {
    if (tableContainerRef.current) {
      setIsContainerReady(true);
    }
    return () => setIsContainerReady(false);
  }, []);

  const columns: ColumnDef<PublishedTableData>[] = [
    {
      accessorKey: "ad",
      header: "Ad",
      cell: ({ row }) => <RenderPublishAdCreative publishAdCreative={row.original.ad} />,
    },
    {
      accessorKey: "publishingAsset",
      header: "Campaign",
      cell: ({ row }) => (
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col max-w-[280px]">
            <div>
              <span className="font-medium text-[12.6px] leading-[18px] tracking-[0%] text-brand-primary-new">
                Campaign:{" "}
              </span>
              <span className="font-normal text-[12.6px] leading-[18px] tracking-[0%] text-brand-primary-new break-all whitespace-normal">
                {row.original.publishingAsset.campaign.name}
              </span>
            </div>
            <div>
              <span className="font-medium text-[12.6px] leading-[18px] tracking-[0%] text-brand-primary-new">
                Adset:{" "}
              </span>
              <span className="font-normal text-[12.6px] leading-[18px] tracking-[0%] text-brand-primary-new break-all whitespace-normal">
                {row.original.publishingAsset.adset.name}
              </span>
            </div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "primaryText",
      header: "Primary Text",
      cell: ({ row }) => (
        <div className="min-w-[160px] max-w-[280px] font-['Inter'] text-[12.6px] leading-[18px] tracking-[0%] text-brand-primary-new break-words whitespace-normal font-normal">
          {row.original.primaryText.join(" • ")}
        </div>
      ),
    },
    {
      accessorKey: "publishingDate",
      header: "Publishing Date",
      cell: ({ row }) => {
        const date = new Date(row.original.publishingDate);
        const formattedDate = date.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
        return (
          <div className="font-normal text-[12.6px] leading-[18px] tracking-[0%] text-brand-primary-new min-w-[120px]">
            {formattedDate}
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const hasRows = table.getRowModel().rows?.length > 0;

  if (!hasRows) {
    return (
      <EmptyState className="border-x-[1px] border-b-[1px] border-solid border-border-gray rounded-b-[12px]" />
    );
  }

  return (
    <div
      ref={tableContainerRef}
      className="scrollable-table-container overflow-auto max-h-[calc(100vh-204px)] border-x-[1px] border-b-[1px] border-solid border-border-gray rounded-b-[12px]"
      style={{ position: "relative" }}
    >
      <Table
        className="border-separate border-spacing-0 bg-white"
        style={{ display: "grid" }}
      >
        <TableHeader
          className="bg-white z-10"
          style={{ display: "grid", position: "sticky", top: 0 }}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-white flex w-full">
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="border-b border-r border-border-gray last:border-r-0 flex-1 flex items-center"
                >
                  {header.isPlaceholder ? null : (
                    <div className="font-['Inter'] font-medium text-[12.6px] leading-[18px] tracking-[0%] text-[#71717A]">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </div>
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <VirtualizedTableBody
          table={table}
          tableContainerRef={tableContainerRef}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          enabled={isContainerReady}
        />
      </Table>
      {isLoadingMore && (
        <div className="h-[50px] overflow-hidden flex items-center justify-center bg-white">
          <WaveLoader className="w-[100px]" />
        </div>
      )}
    </div>
  );
};

export default PublishedTable;
